import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import './Canvas3D.css';
import { extensaoBrocaHorizontal } from '../../utils/furoTransforms';

// Cores do tema industrial (constante estática, fora do componente)
const COLORS = {
  background: 0x2e3340,
  grid: 0x252d40,
  piece: 0xf0dfc0,
  pieceEdge: 0xc04040,
  furoVerticalTop: 0x1c1c1c,
  furoVerticalBottom: 0xff0000,
  furoHorizontal: 0x448aff,
  selected: 0xFFD600,
  furoDestacado: 0xff6d00,
  bordaCor: 0x00e676,
  bordaPardo: 0xff7f50,
  pinca: 0x4a4a4a,
  pincaDetalhe: 0x2a2a2a,
  contornoBase: 0x90a4ae
};

function Canvas3D({
  peca,
  bordas,
  transformacao,
  selectedFuro,
  onSelectFuro,
  furosDestacados,
}) {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const meshRef = useRef(null);
  const furosGroupRef = useRef(null);
  const animationIdRef = useRef(null);
  const initialLoadRef = useRef(true);

  const [tooltip, setTooltip] = useState(null);

  // Inicializar cena Three.js
  const initScene = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(COLORS.background);
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 10000);
    camera.position.set(500, 400, 500);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: true 
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableRotate = true;
    controls.enablePan = true;
    controls.enableZoom = true;
    controls.zoomToCursor = true; // scroll dá zoom ancorado no cursor do mouse
    // Mapeamento de botões (ajuste do piloto):
    //  - esquerdo/direito sozinhos: nada (esquerdo reservado p/ futura seleção)
    //  - CTRL + esquerdo OU CTRL + direito: girar (habilitado via keydown/keyup abaixo)
    //  - meio (scroll-click): arrastar/pan (com ou sem CTRL)
    //  - scroll: zoom ancorado no cursor (zoomToCursor)
    //
    // ⚠️ O OrbitControls inverte ROTATE<->PAN sozinho quando ctrlKey/metaKey/shiftKey
    // está pressionado no mousedown (ver case MOUSE.ROTATE / MOUSE.PAN no onMouseDown
    // da lib). Por isso, enquanto CTRL estiver ativo, atribuímos abaixo o valor OPOSTO
    // ao que queremos de fato — a lib inverte de novo e o resultado final fica certo.
    // Sem esse cuidado: CTRL+esquerdo virava pan e CTRL+meio virava rotate livre (bug
    // observado no piloto).
    controls.minDistance = 100;
    controls.maxDistance = 2000;
    controls.dampingFactor = 0.08;
    controls.enableDamping = true;
    controlsRef.current = controls;

    // CTRL segurado habilita girar com o botão esquerdo/direito e mantém o meio como
    // pan (ver nota acima sobre a inversão automática da lib); soltar (ou perder o
    // foco da janela) volta ao estado neutro.
    const setRotateWhileCtrl = (ativo) => {
      controls.mouseButtons.LEFT = ativo ? THREE.MOUSE.PAN : null;
      controls.mouseButtons.RIGHT = ativo ? THREE.MOUSE.PAN : null;
      controls.mouseButtons.MIDDLE = ativo ? THREE.MOUSE.ROTATE : THREE.MOUSE.PAN;
    };
    setRotateWhileCtrl(false);
    const handleKeyDown = (e) => { if (e.key === 'Control') setRotateWhileCtrl(true); };
    const handleKeyUp = (e) => { if (e.key === 'Control') setRotateWhileCtrl(false); };
    const handleBlur = () => setRotateWhileCtrl(false);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);

    // Iluminação
    const ambientLight = new THREE.AmbientLight(0xfff8f0, 0.75);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.0);
    directionalLight.position.set(300, 600, 300);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    scene.add(directionalLight);

    const fillLight = new THREE.DirectionalLight(0xd0e8ff, 0.4);
    fillLight.position.set(-300, 200, -300);
    scene.add(fillLight);

    const topLight = new THREE.DirectionalLight(0xffffff, 0.5);
    topLight.position.set(0, 1000, 0);
    scene.add(topLight);


    // Grupo para furos
    furosGroupRef.current = new THREE.Group();
    scene.add(furosGroupRef.current);

    // Animation loop
    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Resize handler
    const handleResize = () => {
      if (!containerRef.current) return;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
      if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
      if (controls) controls.dispose();
      if (renderer) {
        renderer.dispose();
        if (container && renderer.domElement && container.contains(renderer.domElement)) {
          container.removeChild(renderer.domElement);
        }
      }
    };
  }, []);

  // Criar geometria da peça
  const createPiece = useCallback(() => {
    if (!sceneRef.current || !peca.comprimento || !peca.largura) return;

    // Remover peça anterior
    if (meshRef.current) {
      sceneRef.current.remove(meshRef.current);
      meshRef.current.geometry.dispose();
      meshRef.current.material.dispose();
    }

    // Remover pinça anterior
    const pincaAnterior = sceneRef.current.getObjectByName('pinca');
    if (pincaAnterior) {
      sceneRef.current.remove(pincaAnterior);
    }

    // Remover baseLine anterior (THREE.Line não é isMesh, precisa de remoção explícita)
    const baseLineAnterior = sceneRef.current.getObjectByName('baseLine');
    if (baseLineAnterior) {
      baseLineAnterior.geometry?.dispose();
      baseLineAnterior.material?.dispose();
      sceneRef.current.remove(baseLineAnterior);
    }

    // Remover bordas anteriores
    const objetosParaRemover = [];
    sceneRef.current.traverse((obj) => {
      if (obj.isMesh && !obj.name && obj !== meshRef.current && !furosGroupRef.current?.children.includes(obj)) {
        objetosParaRemover.push(obj);
      }
    });
    objetosParaRemover.forEach(obj => {
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) obj.material.dispose();
      sceneRef.current.remove(obj);
    });

    // ✅ SIMPLIFICADO: Usar dimensões diretas - já vêm transformadas do EditorMPR
    const width = peca.comprimento;
    const depth = peca.largura;
    const height = peca.espessura || 15;

    // Geometria da peça
    const geometry = new THREE.BoxGeometry(width, height, depth);
    
    // Material madeira MDF sólido
    const material = new THREE.MeshStandardMaterial({
      color: COLORS.piece,
      roughness: 0.68,
      metalness: 0.0,
      flatShading: false,
      side: THREE.DoubleSide
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.y = height / 2;
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    // Arestas nítidas da peça
    const edges = new THREE.EdgesGeometry(geometry);
    const edgesMat = new THREE.LineBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.35 });
    const edgeLines = new THREE.LineSegments(edges, edgesMat);
    mesh.add(edgeLines);

    // Contorno vermelho na base da peça
    const baseShape = new THREE.Shape();
    baseShape.moveTo(-width/2, -depth/2);
    baseShape.lineTo(width/2, -depth/2);
    baseShape.lineTo(width/2, depth/2);
    baseShape.lineTo(-width/2, depth/2);
    baseShape.lineTo(-width/2, -depth/2);
    
    const basePoints = baseShape.getPoints();
    const baseGeometry = new THREE.BufferGeometry().setFromPoints(basePoints);
    const baseMaterial = new THREE.LineBasicMaterial({ 
      color: COLORS.contornoBase, 
      linewidth: 3 
    });
    const baseLine = new THREE.Line(baseGeometry, baseMaterial);
    baseLine.name = 'baseLine';
    baseLine.rotation.x = -Math.PI / 2;
    baseLine.position.y = 0.5;
    sceneRef.current.add(baseLine);

    sceneRef.current.add(mesh);
    meshRef.current = mesh;

    // Centralizar câmera apenas na primeira vez — padrão: vista de topo
    if (initialLoadRef.current) {
      const maxDim = Math.max(width, depth, height);
      cameraRef.current.position.set(0, maxDim * 1.5, 0.01); // 0.01 evita gimbal lock
      if (controlsRef.current && controlsRef.current.target) {
        controlsRef.current.target.set(0, height / 2, 0);
        controlsRef.current.update();
      }
      initialLoadRef.current = false;
    }

    // Criar bordas coloridas
    const createBordas = (w, h, d) => {
      if (!bordas || !sceneRef.current) return;
      const espessuraBorda = 2;
      const offset = 0.5;
      const createBordaMesh = (bw, bh, bd, position, color) => {
        if (!color || color === 'nenhum') return null;
        const colorHex = color === 'cor' ? COLORS.bordaCor : COLORS.bordaPardo;
        const geo = new THREE.BoxGeometry(bw, bh, bd);
        const mat = new THREE.MeshStandardMaterial({ color: colorHex, roughness: 0.5 });
        const m = new THREE.Mesh(geo, mat);
        m.position.set(...position);
        return m;
      };
      const topoMesh     = createBordaMesh(w, h, espessuraBorda, [0, h/2, -d/2 - offset], bordas.topo);
      const baixoMesh    = createBordaMesh(w, h, espessuraBorda, [0, h/2,  d/2 + offset], bordas.baixo);
      const esquerdaMesh = createBordaMesh(espessuraBorda, h, d, [-w/2 - offset, h/2, 0], bordas.esquerda);
      const direitaMesh  = createBordaMesh(espessuraBorda, h, d, [ w/2 + offset, h/2, 0], bordas.direita);
      [topoMesh, baixoMesh, esquerdaMesh, direitaMesh].forEach(m => { if (m) sceneRef.current.add(m); });
    };
    createBordas(width, height, depth);

    // Criar pinça/garra
    const createPinca = (w, h, d) => {
      if (!sceneRef.current) return;
      const pincaGroup = new THREE.Group();
      pincaGroup.name = 'pinca';
      const pincaWidth = Math.min(30, w * 0.06);
      const pincaDepth = 12;
      const pincaHeight = h + 15;
      const metalMaterial     = new THREE.MeshStandardMaterial({ color: COLORS.pinca,        roughness: 0.3, metalness: 0.8 });
      const metalDarkMaterial = new THREE.MeshStandardMaterial({ color: COLORS.pincaDetalhe, roughness: 0.4, metalness: 0.7 });
      [-w / 4, w / 4].forEach((posX) => {
        const meshVertical = new THREE.Mesh(new THREE.BoxGeometry(pincaWidth, pincaHeight, pincaDepth), metalMaterial);
        meshVertical.position.set(posX, pincaHeight / 2, -d / 2 - pincaDepth / 2 - 15);
        pincaGroup.add(meshVertical);
        const meshContato = new THREE.Mesh(new THREE.BoxGeometry(pincaWidth, h, 8), metalDarkMaterial);
        meshContato.position.set(posX, h / 2, -d / 2 - 10);
        pincaGroup.add(meshContato);
        const cabecaMesh = new THREE.Mesh(new THREE.BoxGeometry(pincaWidth + 10, 8, pincaDepth + 6), metalMaterial);
        cabecaMesh.position.set(posX, pincaHeight + 4, -d / 2 - pincaDepth / 2 - 15);
        pincaGroup.add(cabecaMesh);
        const indicadorMaterial = new THREE.MeshStandardMaterial({ color: 0xffcc00, roughness: 0.5, metalness: 0.3, emissive: 0xffcc00, emissiveIntensity: 0.2 });
        const indicadorMesh = new THREE.Mesh(new THREE.BoxGeometry(pincaWidth + 6, 4, 4), indicadorMaterial);
        indicadorMesh.position.set(posX, pincaHeight + 10, -d / 2 - pincaDepth / 2 - 15);
        pincaGroup.add(indicadorMesh);
      });
      sceneRef.current.add(pincaGroup);
    };
    createPinca(width, height, depth);

  }, [peca, bordas]);

  // Criar furos
  const createFuros = useCallback(() => {
    if (!furosGroupRef.current || !peca.comprimento || !peca.largura) return;

    // Limpar furos anteriores
    while (furosGroupRef.current.children.length > 0) {
      const child = furosGroupRef.current.children[0];
      if (child.geometry) child.geometry.dispose();
      if (child.material) child.material.dispose();
      furosGroupRef.current.remove(child);
    }

    const espessura = peca.espessura || 15;

    // Dimensões da peça (já rotacionadas, vêm do estado peca)
    const baseWidth = peca.comprimento;
    const baseDepth = peca.largura;

    // ✅ REMOVIDO: função transformCoord - os furos já vêm transformados!

    // Furos verticais — expande grupos (quantidade > 1) para renderização correta
    const furosVertExpandidos = [];
    peca.furos?.forEach((furo, groupIndex) => {
      const qtd = furo.quantidade || 1;
      if (qtd <= 1) {
        furosVertExpandidos.push({ ...furo, _groupIndex: groupIndex });
      } else {
        const dist = furo.distancia || 0;
        const dir = furo.direcao_replicacao || 'x';
        for (let n = 0; n < qtd; n++) {
          furosVertExpandidos.push({
            ...furo,
            x: dir === 'x' ? furo.x + n * dist : furo.x,
            y: dir === 'y' ? furo.y - n * dist : furo.y,
            _groupIndex: groupIndex,
          });
        }
      }
    });

    furosVertExpandidos.forEach((furo) => {
      const groupIndex = furo._groupIndex;
      const groupFuro = peca.furos[groupIndex];
      const isSelected = selectedFuro?.tipo === 'vertical' && (
        selectedFuro?.uid ? selectedFuro.uid === groupFuro?.uid : selectedFuro?.index === groupIndex
      );
      const isHighlighted = !isSelected && furosDestacados?.has(groupFuro?.uid);
      const isBottom = furo.lado === 'LI' || furo.lado === 'LSU';

      const radius = furo.diametro / 2;
      const depth = Math.min(furo.profundidade || 11, espessura);

      const furoColor = isSelected
        ? COLORS.selected
        : (isHighlighted ? COLORS.furoDestacado : (isBottom ? COLORS.furoVerticalBottom : COLORS.furoVerticalTop));

      const posX = furo.x - baseWidth / 2;
      const posZ = furo.y - baseDepth / 2;
      const posY = isBottom ? depth / 2 : espessura - depth / 2;
      const surfaceY = isBottom ? -0.3 : espessura + 0.3;
      const ud = { tipo: 'vertical', index: groupIndex, uid: groupFuro?.uid, furo: groupFuro };

      // Cilindro escuro — simula profundidade real do furo
      const cylGeo = new THREE.CylinderGeometry(radius, radius, depth, 32);
      const cylMat = new THREE.MeshStandardMaterial({
        color: 0x0a0a0a,
        roughness: 0.95,
        emissive: furoColor,
        emissiveIntensity: isSelected ? 0.35 : (isHighlighted ? 0.25 : 0.06)
      });
      const mesh = new THREE.Mesh(cylGeo, cylMat);
      mesh.position.set(posX, posY, posZ);
      mesh.userData = ud;
      furosGroupRef.current.add(mesh);

      // Disco escuro na superfície — furo real (escuro = profundidade)
      const discGeo = new THREE.CircleGeometry(radius, 48);
      const discMat = new THREE.MeshBasicMaterial({
        color: isSelected ? COLORS.selected : 0x141414,
        side: THREE.DoubleSide,
        depthWrite: false
      });
      const disc = new THREE.Mesh(discGeo, discMat);
      disc.rotation.x = -Math.PI / 2;
      disc.position.set(posX, surfaceY, posZ);
      disc.renderOrder = 1;
      disc.userData = ud;
      furosGroupRef.current.add(disc);

      // Anel colorido fino ao redor — identifica tipo (preto=topo, vermelho=base)
      const ringGeo = new THREE.RingGeometry(radius + 0.3, radius + 1.5, 48);
      const ringMat = new THREE.MeshBasicMaterial({
        color: isSelected ? 0xffffff : furoColor,
        side: THREE.DoubleSide,
        depthWrite: false
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.rotation.x = -Math.PI / 2;
      ring.position.set(posX, surfaceY + 0.15, posZ);
      ring.renderOrder = 2;
      ring.userData = ud;
      furosGroupRef.current.add(ring);

      // Indicador pequeno na face superior para furos inferiores (visível de topo)
      if (isBottom) {
        const dotRadius = Math.max(radius * 0.35, 1.5);
        const dotGeo = new THREE.CircleGeometry(dotRadius, 24);
        const dotMat = new THREE.MeshBasicMaterial({
          color: isSelected ? COLORS.selected : COLORS.furoVerticalBottom,
          side: THREE.DoubleSide,
          depthWrite: false,
          transparent: true,
          opacity: isSelected ? 1.0 : 0.75
        });
        const dot = new THREE.Mesh(dotGeo, dotMat);
        dot.rotation.x = -Math.PI / 2;
        dot.position.set(posX, espessura + 0.4, posZ);
        dot.renderOrder = 3;
        dot.userData = ud;
        furosGroupRef.current.add(dot);

        const dotRingGeo = new THREE.RingGeometry(dotRadius + 0.2, dotRadius + 0.8, 24);
        const dotRingMat = new THREE.MeshBasicMaterial({
          color: isSelected ? 0xffffff : COLORS.furoVerticalBottom,
          side: THREE.DoubleSide,
          depthWrite: false,
          transparent: true,
          opacity: isSelected ? 1.0 : 0.9
        });
        const dotRing = new THREE.Mesh(dotRingGeo, dotRingMat);
        dotRing.rotation.x = -Math.PI / 2;
        dotRing.position.set(posX, espessura + 0.55, posZ);
        dotRing.renderOrder = 4;
        dotRing.userData = ud;
        furosGroupRef.current.add(dotRing);
      }
    });

    // Furos horizontais — expande grupos para renderização correta
    const furosHorizExpandidos = [];
    peca.furosHorizontais?.forEach((furo, groupIndex) => {
      const qtd = furo.quantidade || 1;
      if (qtd <= 1) {
        furosHorizExpandidos.push({ ...furo, _groupIndex: groupIndex });
      } else {
        const dist = furo.distancia || 0;
        const dir = furo.direcao_replicacao || 'x';
        for (let n = 0; n < qtd; n++) {
          furosHorizExpandidos.push({
            ...furo,
            x: dir === 'x' ? furo.x + n * dist : furo.x,
            y: dir === 'y' ? furo.y - n * dist : furo.y,
            _groupIndex: groupIndex,
          });
        }
      }
    });

    furosHorizExpandidos.forEach((furo) => {
      const groupIndex = furo._groupIndex;
      const groupFuro = peca.furosHorizontais[groupIndex];
      const isSelected = selectedFuro?.tipo === 'horizontal' && (
        selectedFuro?.uid ? selectedFuro.uid === groupFuro?.uid : selectedFuro?.index === groupIndex
      );

      const hColor = isSelected ? COLORS.selected : COLORS.furoHorizontal;
      const radius = furo.diametro / 2;
      const depth = furo.profundidade || 22;
      // Borda de entrada derivada da GEOMETRIA (x/y vs. dimensões da peça),
      // não do campo `lado`/BM — BM não é rótulo confiável de borda (duas
      // convenções reais coexistem entre famílias de produto, ver
      // docs/referencia/formato-mpr/campo-bm-horizontal.md §5/§8).
      const { eixo, borda } = extensaoBrocaHorizontal(furo, baseWidth, baseDepth);
      const furoX = furo.x === 'x' ? baseWidth : furo.x;
      const posX = furoX - baseWidth / 2;
      const posZ = furo.y - baseDepth / 2;
      const posY = furo.z || espessura / 2;
      const ud = { tipo: 'horizontal', index: groupIndex, uid: groupFuro?.uid, furo: groupFuro };

      // Cilindro escuro — profundidade do furo horizontal
      const geometry = new THREE.CylinderGeometry(radius, radius, depth, 32);
      const material = new THREE.MeshStandardMaterial({
        color: 0x0a0a0a,
        roughness: 0.95,
        emissive: hColor,
        emissiveIntensity: isSelected ? 0.4 : 0.08
      });
      const mesh = new THREE.Mesh(geometry, material);

      if (eixo === 'X') {
        mesh.rotation.z = Math.PI / 2;
        mesh.position.set(
          borda === 'XL' ? baseWidth/2 - depth/2 : -baseWidth/2 + depth/2,
          posY,
          posZ
        );
      } else {
        mesh.rotation.x = Math.PI / 2;
        mesh.position.set(
          posX,
          posY,
          borda === 'YL' ? baseDepth/2 - depth/2 : -baseDepth/2 + depth/2
        );
      }
      mesh.userData = ud;
      furosGroupRef.current.add(mesh);

      // Disco escuro na face lateral — furo horizontal
      const discGeo = new THREE.CircleGeometry(radius, 48);
      const discMat = new THREE.MeshBasicMaterial({ color: isSelected ? COLORS.selected : 0x141414, side: THREE.DoubleSide, depthWrite: false });
      const disc = new THREE.Mesh(discGeo, discMat);
      disc.renderOrder = 1;
      disc.userData = ud;

      const faceOffset = 0.4;
      if (eixo === 'X' && borda === 'XL') {
        disc.rotation.y = Math.PI / 2;
        disc.position.set(baseWidth/2 + faceOffset, posY, posZ);
      } else if (eixo === 'X' && borda === 'X0') {
        disc.rotation.y = -Math.PI / 2;
        disc.position.set(-baseWidth/2 - faceOffset, posY, posZ);
      } else if (eixo === 'Y' && borda === 'YL') {
        disc.rotation.y = 0;
        disc.position.set(posX, posY, baseDepth/2 + faceOffset);
      } else {
        disc.rotation.y = Math.PI;
        disc.position.set(posX, posY, -baseDepth/2 - faceOffset);
      }
      furosGroupRef.current.add(disc);

      // Anel colorido fino — identifica tipo (azul = horizontal)
      const ringGeo = new THREE.RingGeometry(radius + 0.3, radius + 1.5, 48);
      const ringMat = new THREE.MeshBasicMaterial({ color: isSelected ? 0xffffff : hColor, side: THREE.DoubleSide, depthWrite: false });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.renderOrder = 2;
      ring.userData = ud;
      if (eixo === 'X' && borda === 'XL') {
        ring.rotation.y = Math.PI / 2;
        ring.position.set(baseWidth/2 + faceOffset + 0.15, posY, posZ);
      } else if (eixo === 'X' && borda === 'X0') {
        ring.rotation.y = -Math.PI / 2;
        ring.position.set(-baseWidth/2 - faceOffset - 0.15, posY, posZ);
      } else if (eixo === 'Y' && borda === 'YL') {
        ring.rotation.y = 0;
        ring.position.set(posX, posY, baseDepth/2 + faceOffset + 0.15);
      } else {
        ring.rotation.y = Math.PI;
        ring.position.set(posX, posY, -baseDepth/2 - faceOffset - 0.15);
      }
      furosGroupRef.current.add(ring);
    });

    // Rasgos
    peca.rasgos?.forEach((rasgo, index) => {
      const w = rasgo.direcao === 'X' ? rasgo.comprimento : rasgo.largura;
      const d = rasgo.direcao === 'X' ? rasgo.largura : rasgo.comprimento;
      const h = rasgo.profundidade;

      const geometry = new THREE.BoxGeometry(w, h, d);
      const material = new THREE.MeshStandardMaterial({
        color: rasgo.face === 'superior' ? 0xFF8C00 : 0x3355cc,
        roughness: 0.4,
        transparent: true,
        opacity: 0.75
      });

      const mesh = new THREE.Mesh(geometry, material);

      const posX = rasgo.x - baseWidth / 2;
      const posZ = rasgo.y - baseDepth / 2;
      const posY = rasgo.face === 'superior'
        ? espessura - h / 2
        : h / 2;

      mesh.position.set(posX, posY, posZ);
      mesh.userData = { tipo: 'rasgo', index, rasgo };

      furosGroupRef.current.add(mesh);
    });

  }, [peca, selectedFuro, furosDestacados]);

  // Raycasting para seleção de furos
  const handleClick = useCallback((event) => {
    if (!rendererRef.current || !cameraRef.current || !furosGroupRef.current) return;

    const rect = rendererRef.current.domElement.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, cameraRef.current);

    const intersects = raycaster.intersectObjects(furosGroupRef.current.children);

    if (intersects.length > 0) {
      const { tipo, index, uid, furo } = intersects[0].object.userData;
      if (tipo !== 'rasgo') {
        onSelectFuro?.({ tipo, index, uid, furo });
      }
    }
  }, [onSelectFuro]);

  // Tooltip no hover
  const handleMouseMove = useCallback((event) => {
    if (!rendererRef.current || !cameraRef.current || !furosGroupRef.current) return;

    const rect = rendererRef.current.domElement.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, cameraRef.current);

    const intersects = raycaster.intersectObjects(furosGroupRef.current.children);

    if (intersects.length > 0) {
      const { tipo, index, furo, rasgo } = intersects[0].object.userData;
      setTooltip({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
        tipo,
        index,
        furo,
        rasgo
      });
    } else {
      setTooltip(null);
    }
  }, []);

  // Inicializar cena
  useEffect(() => {
    const cleanup = initScene();
    return cleanup;
  }, [initScene]);

  // Atualizar peça quando dados mudam
  useEffect(() => {
    createPiece();
  }, [createPiece]);

  // Atualizar furos quando dados mudam
  useEffect(() => {
    createFuros();
  }, [createFuros]);

  // Event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('click', handleClick);
    container.addEventListener('mousemove', handleMouseMove);

    return () => {
      container.removeEventListener('click', handleClick);
      container.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleClick, handleMouseMove]);

  return (
    <div className="canvas3d-container" ref={containerRef}>
      {/* Tooltip flutuante */}
      {tooltip && (
        <div
          className="furo-tooltip"
          style={{
            left: tooltip.x + 15,
            top: tooltip.y - 10
          }}
        >
          {tooltip.tipo === 'rasgo' ? (
            <>
              <div className="tooltip-header">▬ Rasgo R{tooltip.index + 1}</div>
              <div className="tooltip-body">
                <span>{tooltip.rasgo.largura}×{tooltip.rasgo.comprimento}mm</span>
                <span>Prof: {tooltip.rasgo.profundidade}mm</span>
                <span>Face: {tooltip.rasgo.face}</span>
                <span>Dir: {tooltip.rasgo.direcao}</span>
              </div>
            </>
          ) : (
            <>
              <div className="tooltip-header">
                Furo {tooltip.tipo === 'vertical' ? 'V' : 'H'}{tooltip.index + 1}
              </div>
              <div className="tooltip-body">
                <span>Ø{tooltip.furo.diametro}mm</span>
                <span>Prof: {tooltip.furo.profundidade || 11}mm</span>
                <span>Pos: ({tooltip.furo.x}, {tooltip.furo.y})</span>
                {tooltip.furo.lado && <span>Lado: {tooltip.furo.lado}</span>}
              </div>
            </>
          )}
        </div>
      )}

      {/* Gizmo de eixos — vista de topo */}
      <svg className="axis-gizmo" width="105" height="105" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="105" height="105" rx="8" fill="rgba(10,12,18,0.78)"/>

        {/* X — vermelho, aponta para a direita */}
        <line x1="46" y1="62" x2="87" y2="62" stroke="#ff4444" strokeWidth="2.5" strokeLinecap="round"/>
        <polygon points="87,62 80,58.5 80,65.5" fill="#ff4444"/>
        <text x="95" y="62" fill="#ff4444" fontSize="13" fontWeight="700" fontFamily="monospace" textAnchor="middle" dominantBaseline="central">X</text>

        {/* Y — verde, aponta para cima */}
        <line x1="46" y1="62" x2="46" y2="20" stroke="#00ff62" strokeWidth="2.5" strokeLinecap="round"/>
        <polygon points="46,20 42,27 50,27" fill="#00ff62"/>
        <text x="46" y="11" fill="#00ff62" fontSize="13" fontWeight="700" fontFamily="monospace" textAnchor="middle" dominantBaseline="central">Y</text>

        {/* Z — azul, sai da tela em direção ao observador (símbolo ⊙) */}
        <circle cx="46" cy="62" r="8" stroke="#4fc3f7" strokeWidth="2" fill="rgba(10,12,18,0.6)"/>
        <circle cx="46" cy="62" r="2.5" fill="#4fc3f7"/>
        <text x="22" y="80" fill="#4fc3f7" fontSize="13" fontWeight="700" fontFamily="monospace" textAnchor="middle" dominantBaseline="central">Z</text>
      </svg>

      {/* Info da peça */}
      {peca.comprimento && peca.largura && (
        <div className="piece-info">
          <span>{peca.comprimento} × {peca.largura} × {peca.espessura || 15}mm</span>
        </div>
      )}

      {/* Controles de visualização */}
      <div className="view-controls">
        <button 
          className="view-btn" 
          onClick={() => {
            if (cameraRef.current && controlsRef.current) {
              const maxDim = Math.max(peca.comprimento || 100, peca.largura || 100);
              cameraRef.current.position.set(0, maxDim * 1.5, 0);
              controlsRef.current.target.set(0, (peca.espessura || 15) / 2, 0);
              controlsRef.current.update();
            }
          }}
          title="Vista Superior (Topo)"
        >
          Topo
        </button>
        <button 
          className="view-btn"
          onClick={() => {
            if (cameraRef.current && controlsRef.current) {
              const maxDim = Math.max(peca.comprimento || 100, peca.largura || 100);
              cameraRef.current.position.set(0, maxDim * 0.3, maxDim * 1.5);
              controlsRef.current.target.set(0, (peca.espessura || 15) / 2, 0);
              controlsRef.current.update();
            }
          }}
          title="Vista Frontal"
        >
          Frente
        </button>
        <button 
          className="view-btn"
          onClick={() => {
            if (cameraRef.current && controlsRef.current) {
              const maxDim = Math.max(peca.comprimento || 100, peca.largura || 100);
              cameraRef.current.position.set(maxDim * 1.5, maxDim * 0.3, 0);
              controlsRef.current.target.set(0, (peca.espessura || 15) / 2, 0);
              controlsRef.current.update();
            }
          }}
          title="Vista Lateral"
        >
          Lateral
        </button>
        <button 
          className="view-btn"
          onClick={() => {
            if (cameraRef.current && controlsRef.current) {
              const maxDim = Math.max(peca.comprimento || 100, peca.largura || 100);
              cameraRef.current.position.set(maxDim * 1.2, maxDim * 0.8, maxDim * 1.2);
              controlsRef.current.target.set(0, (peca.espessura || 15) / 2, 0);
              controlsRef.current.update();
            }
          }}
          title="Vista 3D"
        >
          3D
        </button>
      </div>
    </div>
  );
}

export default Canvas3D;